import { Button, Typography } from "@mui/material";
import { Box } from "@mui/system";
import CentredSpinnerBox from "components/CentredSpinnerBox";
import PriceBox from "components/PriceBox";
import ServiceVariantDetails from "components/ServiceVariantDetails";
import fromTofyTimes from "helpers/datetime/fromTofyTimes";
import { DateTime } from "luxon";
import SubscriptionRedemptionTag from "pages/AppointmentPage/SubscriptionRedemptionTag";
import AppointmentArticleContainer from "pages/ReviewAppointmentPage/AppointmentArticleContainer";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "store";
import { selectPackageVariantById } from "store/selectors";
import { Article } from "types/Article";

type AppointmentPackageArticleType = {
  article: Article;
  slots: ArticleSlot[];

  shouldShowPrice?: boolean;
  isExpandable?: boolean;
  isDetailsPage?: boolean;
};

const AppointmentPackageArticle = ({
  article,
  slots,
  shouldShowPrice = true,
  isExpandable = true,
  isDetailsPage = false,
}: AppointmentPackageArticleType) => {
  const { t } = useTranslation(["common", "glossary"]);

  const [isExpanded, setIsExpanded] = useState(false);

  const packageVariant = useSelector(selectPackageVariantById(article.packageVariant ?? ""));

  if (!packageVariant) return <CentredSpinnerBox />;

  const startOfFirstService = slots[0]?.start;
  const endOfLastService = slots[slots.length - 1]?.end;

  return (
    <AppointmentArticleContainer>
      <Box display="flex" flexDirection="column" justifyContent="center" width="100%" rowGap={2}>
        <Box display="flex" flexDirection="row" gap={1} justifyContent="space-between">
          <Box display="flex" flexDirection="column" rowGap={1} height="100%">
            <Typography variant="h4">{t(packageVariant.t.name)}</Typography>

            <Typography variant="primary">{`${fromTofyTimes(
              DateTime.fromISO(startOfFirstService),
              DateTime.fromISO(endOfLastService)
            )} • ${article.slots.length} ${t("glossary:services")}`}</Typography>
          </Box>

          {shouldShowPrice && (
            <PriceBox
              isFree={!!article.subscriptionRedemption}
              value={Number(packageVariant.price)}
              decimalStrategy="trim_zeros"
              variant="h4"
              textSx={{ color: isDetailsPage ? "text.primary" : "text.secondary" }}
            />
          )}
        </Box>

        {article.subscriptionRedemption && (
          <SubscriptionRedemptionTag title={article.subscriptionRedemption.name} />
        )}

        {isExpandable && (
          <>
            {isExpanded &&
              slots.map((slot, index) => (
                <Box
                  key={slot.serviceVariant}
                  display="flex"
                  flexDirection="column"
                  gap={1}
                  justifyContent="center"
                >
                  <ServiceVariantDetails
                    slot={slot}
                    index={index + 1}
                    isStaffSelectedByClient={slot.isStaffSelectedByClient}
                  />
                </Box>
              ))}

            <Box display="flex" flexDirection="row" gap={1} justifyContent="center">
              <Button
                fullWidth
                variant="outlined"
                color="secondary"
                onClick={() => setIsExpanded(!isExpanded)}
                size="small"
              >
                {isExpanded ? t("common:showLess") : t("common:showMore")}
              </Button>
            </Box>
          </>
        )}
      </Box>
    </AppointmentArticleContainer>
  );
};

export default AppointmentPackageArticle;
